import React, { useState } from "react";
import {
  confirmConfigurationsState,
  deleteConfiguration,
  isDirtyStateOtherIndex,
  resetSate,
  setEdit,
  TenantConfiguration,
  tenantConfigurationState,
  updateConfigurationsState,
} from "../../redux/feature/settings/configurations-slice";
import { useAppDispatch, useAppSelector } from "../../redux/hooks/hooks";
import { TextField } from "@fluentui/react/lib/TextField";
import { formatMessage } from "../../utils/helpers/LanguageHelper";
import { CommandBar, CommandBarButton, ICommandBarItemProps, Stack } from "@fluentui/react";
import { CustomModal } from "../layout/ActionModal";
import LabelWithDotsAndLine from "../generic/LabelWithDotsAndLine";
import ActiveConfiguration from "./ActiveConfiguration";
import { getHTMLElementAsButtonProps } from "../../common/accessibility/HTMLElementAsButtonARIA";
import { sanitizeUserInput } from "../../utils/helpers/SanitizeHelper";

export interface ConfigurationProps {
  index: number;
  inUse: boolean;
  authenticated: boolean;
}

export interface ConfigurationState {
  deleteModalStatus: "opened" | "closed";
  advancedMode: true | false;
}

export const Configuration: React.FunctionComponent<ConfigurationProps> = (props) => {
  const configurationState = useAppSelector(tenantConfigurationState);
  const dispatch = useAppDispatch();
  const [state, setState] = useState<ConfigurationState>({ deleteModalStatus: "closed", advancedMode: false });
  const configurations = configurationState.configurations;
  const conf = configurations[props.index];
  const validation = configurationState.validations[props.index];
  const dirty = configurationState.dirty[props.index];
  const configurationInUse = props.inUse;
  const authenticated = props.authenticated;

  const errorsMap = new Map<string, string>(
    validation.propertyValidationEntries.map((v) => [v.propertyName, v.message])
  );

  const _items: ICommandBarItemProps[] = [
    {
      key: "Cancel",
      text: formatMessage("cof.base.cancel"),
      iconProps: { iconName: "Cancel" },
      onClick: () => cancel(),
    },
    {
      disabled: !dirty || !validation.valid,
      key: "Save",
      text: formatMessage("cof.base.save"),
      iconProps: { iconName: "Save" },
      onClick: () => save(),
    },
  ];

  function cancel() {
    dispatch(resetSate());
  }

  function save() {
    updateState({
      name: sanitizeUserInput(conf.name),
      url: sanitizeUserInput(conf.url),
      tenantId: sanitizeUserInput(conf.tenantId),
      idpUrl: sanitizeUserInput(conf.idpUrl),
      idpClientId: sanitizeUserInput(conf.idpClientId),
    });
    dispatch(confirmConfigurationsState(props.index));
  }

  function updateState(updatedConf: TenantConfiguration) {
    dispatch(
      updateConfigurationsState({
        index: props.index,
        tenantConfiguration: [
          ...configurations.slice(0, props.index),
          updatedConf,
          ...configurations.slice(props.index + 1),
        ],
      })
    );
  }

  const deleteConf = () => {
    dispatch(deleteConfiguration(props.index));
    setState({ ...state, deleteModalStatus: "closed" });
  };

  const openDeleteModal = () => {
    setState({ ...state, deleteModalStatus: "opened" });
  };

  const toggleAdvancedMode = () => {
    setState({ ...state, advancedMode: !state.advancedMode });
  };

  const setName = (name) => {
    updateState({ ...conf, name: name });
  };

  const setUrl = (url) => {
    updateState({ ...conf, url: url });
  };

  const setIdpUrl = (idpUrl) => {
    updateState({ ...conf, idpUrl: idpUrl });
  };

  const setIdpClientId = (idpClientId) => {
    updateState({ ...conf, idpClientId: idpClientId });
  };

  const setTenantId = (tenantId) => {
    updateState({ ...conf, tenantId: tenantId });
  };

  const edit = () => {
    if (!isDirtyStateOtherIndex(configurationState, props.index)) {
      dispatch(setEdit(props.index));
    }
  };

  const deleteDisabled = () => {
    return dirty || configurationInUse;
  };

  function getDeleteStyle() {
    if (deleteDisabled()) {
      return {
        icon: { color: "rgb(200, 198, 196)" },
        label: { color: "rgb(200, 198, 196)" },
        iconHovered: { color: "rgb(200, 198, 196)" },
      };
    } else {
      return {
        icon: { color: "red" },
        label: { color: "red" },
        iconHovered: { color: "red" },
      };
    }
  }

  function getAdvancedConfStyle() {
    return {
      icon: { color: "rgb(128, 128, 128)" },
      iconHovered: { color: "rgb(128, 128, 128)" },
      label: {
        color: "rgb(128, 128, 128)",
        textDecoration: "underline",
      },
    };
  }

  const renderActiveConfiguration = () => {
    return configurationInUse && validation.valid;
  };

  const stackStyles = {
    root: {
      justifyContent: renderActiveConfiguration() ? "space-between" : "right",
    },
  };

  const commandBarStyles = {
    root: {
      paddingTop: "5pt",
      float: "right",
    },
  };

  if (state.deleteModalStatus === "closed") {
    if (configurationState.edit[props.index])
      return (
        <>
          <Stack horizontal styles={stackStyles}>
            {renderActiveConfiguration() && (
              <ActiveConfiguration tooltip={"In use"} authenticated={authenticated}></ActiveConfiguration>
            )}
            <span style={{ float: "right" }}>
              <CommandBarButton
                iconProps={{ iconName: "Delete" }}
                text={"Delete"}
                styles={getDeleteStyle()}
                disabled={deleteDisabled()}
                onClick={openDeleteModal}
              />
            </span>
          </Stack>
          <div className="ms-Grid" dir="ltr">
            <div className="ms-Grid-row">
              <div className="ms-Grid-col ms-sm12 ms-md12 ms-lg12">
                <TextField
                  required
                  label={formatMessage("cof.base.displayname")}
                  placeholder={formatMessage("cof.base.displayname")}
                  value={conf.name}
                  disabled={isDirtyStateOtherIndex(configurationState, props.index)}
                  onChange={(e) => setName(e.currentTarget.value)}
                  errorMessage={formatMessage(errorsMap.get("name"))}
                />
              </div>
            </div>
            <div className="ms-Grid-row">
              <div className="ms-Grid-col ms-sm6 ms-md6 ms-lg6">
                <TextField
                  required
                  label={formatMessage("cof.access.manageconfigurations.cchtagetiktenantid")}
                  placeholder={formatMessage("cof.access.manageconfigurations.cchtagetiktenantid")}
                  value={conf.tenantId}
                  disabled={isDirtyStateOtherIndex(configurationState, props.index)}
                  onChange={(e) => setTenantId(e.currentTarget.value)}
                  errorMessage={formatMessage(errorsMap.get("tenantId"))}
                />
              </div>
              <div className="ms-Grid-col ms-sm6 ms-md6 ms-lg6">
                <TextField
                  required
                  label={formatMessage("cof.access.manageconfigurations.cchtagetikclientid")}
                  placeholder={formatMessage("cof.access.manageconfigurations.cchtagetikclientid")}
                  value={conf.idpClientId}
                  disabled={isDirtyStateOtherIndex(configurationState, props.index)}
                  onChange={(e) => setIdpClientId(e.currentTarget.value)}
                  errorMessage={formatMessage(errorsMap.get("idpClientId"))}
                />
              </div>
            </div>
            {state.advancedMode && (
              <>
                <div className="ms-Grid-row">
                  <div className="ms-Grid-col ms-sm12 ms-md12 ms-lg12">
                    <TextField
                      label={formatMessage("cof.access.manageconfigurations.cchtagetikurl")}
                      placeholder={formatMessage("cof.access.manageconfigurations.cchtagetikurl")}
                      value={conf.url}
                      disabled={isDirtyStateOtherIndex(configurationState, props.index)}
                      onChange={(e) => setUrl(e.currentTarget.value)}
                      errorMessage={formatMessage(errorsMap.get("url"))}
                    />
                  </div>
                </div>
                <div className="ms-Grid-row">
                  <div className="ms-Grid-col ms-sm12 ms-md12 ms-lg12">
                    <TextField
                      label={formatMessage("cof.access.manageconfigurations.idpurl")}
                      placeholder={formatMessage("cof.access.manageconfigurations.idpurl")}
                      value={conf.idpUrl}
                      disabled={isDirtyStateOtherIndex(configurationState, props.index)}
                      onChange={(e) => setIdpUrl(e.currentTarget.value)}
                      errorMessage={formatMessage(errorsMap.get("idpUrl"))}
                    />
                  </div>
                </div>
              </>
            )}
            <CommandBar items={_items} styles={commandBarStyles} onReduceData={() => undefined} />
          </div>
          <div style={{ width: "100%", justifyContent: "start" }}>
            {state.advancedMode && (
              <CommandBarButton
                text={formatMessage("cof.base.hideadvanced")}
                onClick={toggleAdvancedMode}
                styles={getAdvancedConfStyle()}
              />
            )}
            {!state.advancedMode && (
              <CommandBarButton
                text={formatMessage("cof.base.showadvanced")}
                onClick={toggleAdvancedMode}
                styles={getAdvancedConfStyle()}
              />
            )}
          </div>
        </>
      );
    return (
      <div>
        {configurationInUse && (
          <ActiveConfiguration tooltip={"In use"} authenticated={authenticated}></ActiveConfiguration>
        )}
        <div
          {...getHTMLElementAsButtonProps({
            onClick: edit,
          })}
        >
          <LabelWithDotsAndLine text={conf.name} />
        </div>
      </div>
    );
  }
  return (
    <DeleteConfigurationConfirm
      onConfirm={deleteConf}
      onCancel={() => setState({ ...state, deleteModalStatus: "closed" })}
    ></DeleteConfigurationConfirm>
  );
};

interface DeleteConfigurationConfirmProps {
  onCancel: () => void;
  onConfirm: () => void;
}

const DeleteConfigurationConfirm: React.FunctionComponent<DeleteConfigurationConfirmProps> = (props) => {
  return (
    <CustomModal
      modalTitle={formatMessage("cof.access.configurations.configurationdelete")}
      messageTitle={formatMessage("cof.access.configurations.configurationdelete.info")}
      message=""
      type="delete"
      onHide={() => {
        return;
      }}
      actions={[
        {
          id: "cancel_button",
          title: formatMessage("cof.base.noconfirmdelete"),
          onClick: () => {
            props.onCancel();
          },
          variant: "blue",
        },
        {
          id: "confirm_button",
          title: formatMessage("cof.base.confirmdelete"),
          onClick: () => props.onConfirm(),
          variant: "red_filled",
        },
      ]}
    />
  );
};
